/**
 * Report the shape of a visual-review response without interpreting it.
 *
 * `PostA2AResult.body` is `unknown`, and the A2A skill's response contract is
 * declared nowhere in this repo. The agent channel derived from the gateway's
 * own source (`packages/cogo-a2a-gateway/src/adapter.ts`) that it emits exactly
 * two artifact shapes, which parse completely differently:
 *
 *   jsonArtifact             artifactId === schemaVersion,  judgment in parts[0].data
 *   executionResultArtifact  artifactId === 'coogo-execution-result',
 *                            judgment in parts[0].text as a JSON string
 *
 * It could not establish which one this skill takes, and declined to guess. So
 * this describes what actually arrived — ids and the kinds of part present —
 * and never reads a payload. One real call then settles it, and the parser is
 * written against an observation instead of a hope.
 *
 * Every "missing" answer is a distinct word. An absent key and an empty array
 * are different facts: collapsing them would let a wrong access path read as a
 * confident "shape A", which is the exact failure this exists to avoid.
 */

export type EnvelopeSlot = 'absent' | 'not-an-object' | 'object'
export type ArtifactsSlot = 'absent' | 'not-an-array' | 'empty-array' | 'array'

export interface ReviewEnvelopeEntry {
  /** The artifact's declared id, or `null` when it does not carry one. */
  artifactId: string | null
  /** Which part fields are present — 'data', 'text', or whatever else appears. */
  partKinds: string[]
}

export interface ReviewEnvelopeReport {
  result: EnvelopeSlot
  /** Every top-level key of `result`, so a shape nobody predicted is visible. */
  resultKeys: string[]
  artifacts: ArtifactsSlot
  entries: ReviewEnvelopeEntry[]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function describeParts(artifact: unknown): ReviewEnvelopeEntry {
  if (!isRecord(artifact)) return { artifactId: null, partKinds: [] }
  const artifactId =
    typeof artifact.artifactId === 'string' ? artifact.artifactId : null
  const parts = artifact.parts
  if (!Array.isArray(parts)) return { artifactId, partKinds: [] }
  const partKinds: string[] = []
  for (const part of parts) {
    if (!isRecord(part)) continue
    // The KEYS present, not their contents — naming a payload is where a
    // report would start pretending to be a parser.
    for (const key of Object.keys(part)) {
      if (!partKinds.includes(key)) partKinds.push(key)
    }
  }
  return { artifactId, partKinds }
}

export function describeReviewEnvelope(body: unknown): ReviewEnvelopeReport {
  if (!isRecord(body)) {
    return {
      result: body === null || body === undefined ? 'absent' : 'not-an-object',
      resultKeys: [],
      artifacts: 'absent',
      entries: [],
    }
  }
  const result = body.result
  if (!isRecord(result)) {
    return {
      result: result === undefined || result === null ? 'absent' : 'not-an-object',
      resultKeys: [],
      artifacts: 'absent',
      entries: [],
    }
  }
  const resultKeys = Object.keys(result).sort()
  const artifacts = result.artifacts
  if (artifacts === undefined || artifacts === null) {
    return { result: 'object', resultKeys, artifacts: 'absent', entries: [] }
  }
  if (!Array.isArray(artifacts)) {
    return { result: 'object', resultKeys, artifacts: 'not-an-array', entries: [] }
  }
  return {
    result: 'object',
    resultKeys,
    artifacts: artifacts.length === 0 ? 'empty-array' : 'array',
    entries: artifacts.map(describeParts),
  }
}
